import React, { useEffect, useState, useRef } from "react";
import styles from "./profilePage.module.css";
import axios from "axios";
import NewMessageModal from "../../../components/newMessageModal/NewMessageModal";
import PersonData from "../../../components/personData/PersonData";
import ImgComp from "../../../components/images/ImgComp";
import VideoComp from "../../../components/video/VideoComp";
import SearchComp from "../../../components/search/SearchComp";
import LanguagePalsComp from "../../../components/languagePals/LanguagePalsComp";
import PersonalPosts from "../../../components/personalPosts/PersonalPosts";
import defaultImage from "../../../appImges/avatartTemplate/default_img.png";
import { useParams } from "react-router-dom";
import { useUser } from "../../../storage/UserContext";
import { PiDotsThreeOutlineVertical } from "react-icons/pi";
import ModalComp from "../../../components/popUp/ModalComp";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const apiUrl = process.env.REACT_APP_URL;

const OtherUserProfilePage = () => {
  /* This Page is used to display a user's profile page for other users. The displayed information depends on the permissions set by the user who owns the profile page, and in the relationship between him / her and the user who watches the page. This information may contain personal information of the user who owns the profile page, and his / her posts. The user who views the page may send a message to the user who owns the profile page, send a friendship request, follow the user, or block the user. The watching user may also post comments to posts on the page, if their permissions allow that, and may like and share a post. There is also a side component used to search for users on the website. If the active user is a Pal of the viewed user, he / she will also be able to view the user's Pals list. */

  const navigate = useNavigate();
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const dropdownRef = useRef(null); // Ref for the dropdown menu
  const { userData } = useUser(); // Get the logged-in user data from context
  const { username } = useParams(); // Get the username from the URL params
  const [userImages, setUserImages] = useState([]);
  const [offset, setOffset] = useState(0);
  const qty = 5;
  const [profileData, setProfileData] = useState({
    img_link: "",
    username: "",
    first_name: "",
    last_name: "",
    country: "",
    city: "",
    birthdate: "",
    learned_language: "",
    native_language: "",
    description: "",
    education: "",
    hobbies: "",
    video_link: "",
    gender: "",
    role: "",
  });

  const isEditable = userData.username === username;
  const [postsData, setPostsData] = useState([]);

  const [hasMorePosts, setHasMorePosts] = useState(true); // Check if there are more posts to fetch to show/hide the "See More" button

  // State for the "Remove Friend" modal
  const [isModalRemoveFriendOpen, setIsModalRemoveFriendOpen] = useState(false);

  // State for Follow/Unfollow button
  const [followText, setFollowText] = useState("Follow");

  // State to manage Friendship status
  const [arePals, setArePals] = useState(false);
  const [friendshipText, setFriendshipText] = useState("Add Pal");

  // State to manage block status
  const [isBlocked, setIsBlocked] = useState(false);
  const [isModalBlockConfirmOpen, setIsModalBlockConfirmOpen] = useState(false);
  const [blockText, setBlockText] = useState(isBlocked ? "Unblock" : "Block");
  const [isAccessAllowed, setIsAccessAllowed] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showMessageBtnText, setShowMessageBtnText] = useState(false);

  /**
   * Show the new message modal to allow the user to send a message to the user.
   */
  const handleMessageBtnClick = (e) => {
    e.stopPropagation();
    setShowMessageModal(true);
  };

  /**
   * Close the new message modal
   */
  const handleCloseMessageModal = () => {
    setShowMessageModal(false);
  };

  /**
   * Send a message to the user.
   * @param {*} messageData object with properties: type ("text" / "audio"), content, receiverUsername
   */
  const handleSendMessage = async (messageData) => {
    const { type, content, receiverUsername } = messageData;

    if (!content || !receiverUsername) return;

    if (type === "text") {
      try {
        const response = await axios.post(
          `${apiUrl}/messages/new/text`,
          {
            senderUsername: userData.username,
            receiverUsername,
            textContent: content,
          },
          {
            withCredentials: true,
          }
        );

        if (response.status !== 201 || !response.data.success) {
          console.error("Failed to send text message:", response.data.message);
        }
      } catch (error) {
        console.error("Error sending text message:", error);
      }
    } else if (type === "audio") {
      try {
        const formData = new FormData();
        const randomFilename = generateRandomFilename("webm");
        formData.append("audio", content, randomFilename);
        formData.append("senderUsername", userData.username);
        formData.append("receiverUsername", receiverUsername);

        const response = await axios.post(
          `${apiUrl}/messages/new/voice`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
            withCredentials: true,
          }
        );

        if (response.status !== 201 || !response.data.success) {
          console.error("Failed to send voice message:", response.data.message);
        }
      } catch (error) {
        console.error("Error sending voice message:", error);
      }
    }
  };

  /**
   * Generate a random file name. This is used for audio message recordings
   * @param {*} extension - the requested file extension
   * @returns string, which consists of a the generated random string, and the passed extension.
   */
  const generateRandomFilename = (extension) => {
    const randomString = Math.random().toString(36).substring(2, 15);
    return `${randomString}.${extension}`;
  };

  // Fetch profile data and posts data when the component mounts or when the username changes
  useEffect(() => {
    if (!userData || !userData.username || !username) {
      return;
    }


    if (userData.username === username) {
      navigate("/profile");
    }

    const fetchBlockStatus = async () => {
      try {
        const blockRes1 = await axios.get(
          `${apiUrl}/relationships/blocks/${username}/${userData.username}`,
          { withCredentials: true }
        );
        const blockRes2 = await axios.get(
          `${apiUrl}/relationships/blocks/${userData.username}/${username}`,
          { withCredentials: true }
        );
        if (blockRes1.status === 200 && blockRes2.status === 200) {
          const isBlocked =
            blockRes1.data.blockStatus || blockRes2.data.blockStatus;
          setIsAccessAllowed(!isBlocked);
          if (isBlocked) {
            navigate("/home");
          }
        }
      } catch (e) {
        console.error("Failed to fetch block status", e);
      }
    };

    const fetchProfileData = async () => {
      try {
        const userRes = await axios.get(
          `${apiUrl}/users/basic/other/${username}/${userData.username}`,
          { withCredentials: true }
        );

        if (userRes.status === 200) {
          setProfileData(userRes.data);
        }
      } catch (e) {
        console.error("Failed to fetch profile data", e);
      }
    };

    const fetchPostsData = async () => {
      try {
        const response = await axios.get(
          `${apiUrl}/posts/public_shares_and_posts/${username}?qty=5&offset=${offset}`,
          { withCredentials: true }
        );

        if (response.status === 200 && response.data) {
          if (response.data.success === false) {
            setHasMorePosts(false); // Stop further loading if no posts are found
            return; // Exit the function early
          }

          const fetchedPosts = response.data.posts;

          if (Array.isArray(fetchedPosts)) {
            // Filter out any posts with duplicate post_ids
            setPostsData((prevPosts) => {
              const allPosts = [...prevPosts, ...fetchedPosts];
              const uniquePosts = allPosts.filter(
                (post, index, self) =>
                  index === self.findIndex((p) => p.post_id === post.post_id)
              );
              return uniquePosts;
            });

            // Determine if there are more posts to load
            setHasMorePosts(fetchedPosts.length === 5);
          } else {
            console.error("Unexpected data format: posts is not an array");
            setHasMorePosts(false); // Stop further loading if data format is unexpected
          }
        } else {
          console.error(
            "Unexpected response status or missing data:",
            response
          );
          setHasMorePosts(false); // Stop further loading on unexpected response
        }
      } catch (error) {
        // Log and handle the error
        console.error("Failed to fetch posts data:", error);
        setHasMorePosts(false); // Stop further loading on error
      }
    };

    // Reset postsData and offset when username changes
    setPostsData([]);
    setOffset(0);
    setHasMorePosts(true);

    fetchBlockStatus();

    if (isAccessAllowed) {
      fetchProfileData();
      fetchPostsData();
    }
  }, [userData, username, isAccessAllowed, navigate]);

  // Fetch images data
  useEffect(() => {
    const fetchImages = async () => {
      try {
        const imagesRes = await axios.get(
          `${apiUrl}/media/${username}?qty=10`,
          { withCredentials: true }
        );

        if (imagesRes.status === 200) {
          setUserImages(imagesRes.data.images);
        } else {
          console.error("Failed to fetch images:", imagesRes.data.message);
        }
      } catch (e) {
        console.error("Failed to fetch images data", e);
      }
    };

    if (isAccessAllowed && arePals) {
      fetchImages();
    }
  }, [username, isAccessAllowed, arePals]);

  // Fetch additional posts when offset changes
  useEffect(() => {
    const fetchMorePostsData = async () => {
      if (offset === 0) return; // Skip fetching if it's the initial load handled above
      try {
        const postsRes = await axios.get(
          `${apiUrl}/posts/public_shares_and_posts/${username}?qty=5&offset=${offset}`,
          { withCredentials: true }
        );

        if (postsRes.status === 200 && postsRes.data.posts) {
          setPostsData((prevPosts) => {
            const allPosts = [...prevPosts, ...postsRes.data.posts];
            // Filter out duplicate posts by post_id
            const uniquePosts = allPosts.filter(
              (post, index, self) =>
                index === self.findIndex((p) => p.post_id === post.post_id)
            );
            return uniquePosts;
          });

          // Check if more posts are available to load
          setHasMorePosts(postsRes.data.posts.length === 5);
        }
      } catch (e) {
        console.error("Failed to fetch more posts data", e);
      }
    };

    fetchMorePostsData();
  }, [offset, username]);

  useEffect(() => {
    if (!userData.username) {
      return;
    }

    const checkFollowStatus = async () => {
      try {
        const res = await axios.get(
          `${apiUrl}/relationships/follows/${userData.username}/${username}`,
          { withCredentials: true }
        );

        if (res.status === 200 && res.data) {
          setFollowText(res.data.followStatus === 1 ? "Unfollow" : "Follow");
        } else {
          console.warn("Unexpected follow status response:", res);
          setFollowText("Follow"); // Default to "Follow" if response is unexpected
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          console.warn("Follow status not found, assuming not following.");
          setFollowText("Follow"); // Default to "Follow" on 404
        } else {
          console.error("Failed to check follow status:", error);
          setFollowText("Follow"); // Default to "Follow" on any other error
        }
      }
    };

    checkFollowStatus();
  }, [userData.username, username]);

  useEffect(() => {
    if (!userData.username) {
      return;
    }

    const checkFriendshipStatus = async () => {
      try {
        const res = await axios.get(
          `${apiUrl}/relationships/status/${userData.username}/${username}`,
          { withCredentials: true }
        );

        if (res.status === 200 && res.data) {
          setArePals(res.data.status === "Pals");
          setFriendshipText(
            res.data.status === "Pals" ? "Remove Pal" : "Add Pal"
          );
        } else {
          console.warn("Unexpected friendship status response:", res);
          setFriendshipText("Add Pal"); // Default to "Add Friend" if response is unexpected
          setArePals(false);
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          console.warn("Friendship status not found, assuming not friends.");
          setFriendshipText("Add Pal"); // Default to "Add Friend" on 404
          setArePals(false);
        } else {
          console.error("Failed to check friendship status:", error);
          setFriendshipText("Add Pal"); // Default to "Add Friend" on any other error
          setArePals(false);
        }
      }
    };

    checkFriendshipStatus();
  }, [userData.username, username]);

  // Fetch block status on component mount
  useEffect(() => {
    if (!userData.username) {
      return;
    }

    const fetchBlockStatus = async () => {
      try {
        const res = await axios.get(
          `${apiUrl}/relationships/blocks/${userData.username}/${username}`,
          { withCredentials: true }
        );

        if (res.status === 200 && res.data) {
          setIsBlocked(Boolean(res.data.blockStatus));
        } else {
          console.warn("Unexpected block status response:", res);
          setIsBlocked(false); // Default to not blocked if response is unexpected
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          // Handle 404 as no block status found, which might be expected
          console.warn("Block status not found for this user.");
          setIsBlocked(false); // Default to not blocked
        } else {
          console.error("Failed to fetch block status:", error);
          setIsBlocked(false); // Default to not blocked on any other error
        }
      }
    };

    fetchBlockStatus();
  }, [userData.username, username]);

  const toggleModalConfirm = () => {
    setIsModalBlockConfirmOpen(!isModalBlockConfirmOpen);
  };

  // Function to handle block/unblock action
  const handleBlockUnblock = async () => {
    try {
      const newBlockStatus = !isBlocked; // Toggle block status
      const res = await axios.put(
        `${apiUrl}/relationships/block/${userData.username}/${username}`,
        { blockStatus: newBlockStatus },
        { withCredentials: true }
      );

      if (res.status === 200 && res.data.success) {
        setIsBlocked(newBlockStatus);
        setBlockText(newBlockStatus ? "Unblock" : "Block");

        // Redirect to the "Settings" page, specifically to the "Blocked Users" tab
        navigate("/settings", { state: { activeTab: "blocked" } });
      }
    } catch (error) {
      console.error("Failed to update block status", error);
      toast.error("Failed to update block status");
    } finally {
      setIsModalBlockConfirmOpen(false);
    }
  };

  // Toggle Follow Status
  const handleToggleFollow = async () => {
    const newFollowStatus = followText === "Follow";
    try {
      const res = await axios.put(
        `${apiUrl}/relationships/follow/${userData.username}/${username}`,
        { followStatus: newFollowStatus },
        { withCredentials: true }
      );

      if (res.status === 200 && res.data.success) {
        setFollowText(newFollowStatus ? "Unfollow" : "Follow");
        toast.success(
          newFollowStatus
            ? `You are now following ${username}`
            : `You have unfollowed ${username}`
        );
      } else {
        console.error("Failed to update follow status");
      }
    } catch (error) {
      console.error("Error updating follow status:", error);
    }
  };

  // Send Friendship Request
  const handleSendFriendshipRequest = async () => {
    try {
      const res = await axios.post(
        `${apiUrl}/relationships/request/${userData.username}/${username}`,
        {},
        { withCredentials: true }
      );
      if (res.status === 200 && res.data.statusChanged) {
        setFriendshipText("Add Pal");

        // Send notification
        const notificationData = {
          notification_img_src: profileData.img_link,
          message: ` ${userData.username} wants to be your Pal.`,
          type: "palRequest",
          sender_username: userData.username,
          receiver_username: username,
        };

        const res2 = await axios.post("/notifications", notificationData);
        toast.success("Friendship request sent");
      }
    } catch (error) {
      console.error("Failed to send friendship request", error);
      toast.error("Failed to send friendship request");
    }
  };

  // Remove Friend functions
  // Function to open the "Remove Friend" modal
  const openRemoveFriendModal = () => {
    setIsModalRemoveFriendOpen(true); // Open the confirmation modal
  };
  // Function to handle the "Remove Friend" action
  const handleRemoveFriend = async () => {
    try {
      const res = await axios.put(
        `${apiUrl}/relationships/remove/${userData.username}/${username}`,
        {},
        { withCredentials: true }
      );

      if (res.status === 200 && res.data.changed) {
        setArePals(false);
        setFriendshipText("Add Pal");
        toast.success("Pal removed");
      }
    } catch (error) {
      console.error("Failed to remove Pal", error);
      toast.error("Failed to remove Pal");
    } finally {
      setIsModalRemoveFriendOpen(false); // Close the modal after removal
    }
  };

  // Handle "See More" button click
  const handleSeeMore = () => {
    setOffset((prevOffset) => prevOffset + qty); // Increment the offset
  };

  // Handle the dropdown menu toggle
  const toggleDropdown = () => {
    setDropdownVisible(!isDropdownVisible);
  };

  // Set up the event listener using useEffect so that it only runs
  // when the component is mounted and cleans up when the component is unmounted.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownVisible(false); // Close the dropdown if clicked outside
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle the "Send Message" button click
  const toggleModalSendMessage = () => {
    setShowMessageModal(!showMessageModal);
  };

  return (
    <div className={styles.main}>
      {/* hero section  */}
      <div className={styles.backgroundPic}>
        <figure className={styles.personPhoto}>
          <img
            alt={profileData.username}
            src={
              profileData.img_link &&
              profileData.img_link !== "default_img_link"
                ? `${apiUrl}/${profileData.img_link}`
                : defaultImage
            }
            onError={(e) => {
              e.target.src = defaultImage;
              e.target.onerror = null; // Prevents infinite loop if the default image is also not found/>
            }}
          />
          <figcaption>
            <span>
              {profileData.first_name} {profileData.last_name}
            </span>
          </figcaption>
        </figure>
        <div className={styles.dropdownToggle} onClick={toggleDropdown}>
          <PiDotsThreeOutlineVertical className={styles.sign} />
          {isDropdownVisible && (
            <div ref={dropdownRef} className={styles.dropdownMenu}>
              <ul>
                <li onClick={toggleModalSendMessage}>Message</li>
                <li
                  onClick={
                    friendshipText === "Add Pal"
                      ? handleSendFriendshipRequest
                      : openRemoveFriendModal
                  }
                >
                  {friendshipText}
                </li>
                <li onClick={handleToggleFollow}>{followText}</li>
                <li onClick={toggleModalConfirm}>{blockText}</li>
              </ul>
            </div>
          )}
        </div>
      </div>
      {/* person data section  */}
      <PersonData profileData={profileData} isEditable={false} />

      {/* main layout  */}
      <section className={styles.mainLayout}>
        {/* left section  */}
        <div className={styles.left}>
          {arePals && userImages.length > 0 ? (
            <ImgComp
              userImages={userImages}
              setUserImages={setUserImages}
              isEditable={isEditable}
              username={username}
            />
          ) : null}
        </div>
        {/* middle section  */}
        <div className={styles.middle}>
          {postsData && postsData.length > 0 ? (
            <PersonalPosts
              role={userData.role}
              checkTheSameUser={false}
              postsData={postsData}
              username={username}
              userImage={`${apiUrl}/${profileData.img_link}`}
            />
          ) : (
            <p className={styles.noPostsMsg}>
              No posts available. This user hasn't posted anything yet.
            </p>
          )}
          {hasMorePosts && postsData && postsData.length > 0 && (
            <button onClick={handleSeeMore} className={styles.seeMoreBtn}>
              See More
            </button>
          )}
        </div>

        {/* right section  */}
        <div className={styles.right}>
          <VideoComp
            video_url={profileData.video_link}
            username={username}
            isEditable={isEditable}
          />
          <SearchComp />

          {arePals && (
            <LanguagePalsComp isOwn={false} viewedUsername={username} />
          )}
        </div>
      </section>
      {/* All component modal windows: Remove friend, send message and block confirmation*/}
      {/* Remove Friend Confirmation Modal */}
      <ModalComp
        isOpen={isModalRemoveFriendOpen}
        onClose={() => setIsModalRemoveFriendOpen(false)} // Close modal without removing friend
      >
        <p>Are you sure you want to remove this friend?</p>
        <button className={styles.seeMoreBtn} onClick={handleRemoveFriend}>
          Confirm
        </button>{" "}
        <button
          className={styles.seeMoreBtn}
          onClick={() => setIsModalRemoveFriendOpen(false)}
        >
          Cancel
        </button>
      </ModalComp>

      {/* Modal Send Message Component */}
      {showMessageModal && (
        <NewMessageModal
          show={showMessageModal}
          onClose={handleCloseMessageModal}
          onSendMessage={handleSendMessage}
          receiverUsername={username}
        />
      )}
      {/* Modal Block user Component */}
      <ModalComp isOpen={isModalBlockConfirmOpen} onClose={toggleModalConfirm}>
        <p>Are you sure you want to {blockText.toLowerCase()} this user?</p>
        <button className={styles.seeMoreBtn} onClick={handleBlockUnblock}>
          {blockText}
        </button>{" "}
        <button className={styles.seeMoreBtn} onClick={toggleModalConfirm}>
          Cancel
        </button>
      </ModalComp>
    </div>
  );
};

export default OtherUserProfilePage;
